<template>
  <!-- Navigation items (`to` set) render as a real NuxtLink/<a> so they're
       keyboard-focusable, Enter-activatable, and support cmd/middle-click "open
       in new tab"; action items keep the plain <div> + `command`. A disabled
       item never renders as a link (nothing to navigate to). -->
  <component
    :is="rootComponent"
    :to="isLink ? to : undefined"
    class="h-8 gap-2 items-center text-xs px-3 bg-transparent rounded-md mx-1 duration-150 overflow-hidden grid"
    :class="[
      gridColsClass,
      {
        'cursor-pointer': !disabled,
        'opacity-60': disabled,
        'hover:bg-surface-100': !disabled && !danger,
        'hover:bg-error-100': !disabled && danger,
        'text-surface-800': !danger,
        'text-error-600': danger,
      },
    ]"
    @click="onClick"
  >
    <!-- COL 1: PREFIX COLUMN (ICON OR CHECK) -->
    <span
      v-if="hasPrefixColumn"
      class="flex justify-center items-center"
    >
      <AppIcon
        v-if="selected && selectedCheckPrefix"
        icon="fa-regular fa-check"
        class="shrink-0"
      />
      <AppIcon
        v-else-if="icon"
        :icon="icon"
        class="shrink-0"
      />
    </span>

    <!-- COL 2: LABEL -->
    <span class="flex justify-start items-center">
      <span class="truncate">{{ label }}</span>
    </span>

    <!-- COL 3: SUFFIX COLUMN — a trailing element (e.g. a status tag), else the
         selected check / submenu chevron. The slot falls back to the icons, so
         items without a trailing slot render exactly as before. -->
    <span
      class="flex items-center"
      :class="hasTrailing ? 'justify-end' : 'justify-center'"
    >
      <slot name="trailing">
        <AppIcon
          v-if="selected && !selectedCheckPrefix"
          icon="fa-regular fa-check"
          class="shrink-0"
        />
        <AppIcon
          v-else-if="isSubMenu"
          icon="fa-regular fa-chevron-right"
          class="text-2xs"
        />
      </slot>
    </span>
  </component>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

const props = defineProps({
  icon: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isSubMenu: {
    type: Boolean,
    default: false,
  },
  // Navigation target. When set (and not disabled), the item renders as a
  // NuxtLink instead of firing a `command`.
  to: {
    type: [String, Object] as PropType<RouteLocationRaw>,
    default: undefined,
  },
  command: {
    type: Function,
    default: undefined,
  },
  selected: {
    type: Boolean,
    default: undefined,
  },
  selectedCheckPrefix: {
    type: Boolean,
    default: false,
  },
  danger: {
    type: Boolean,
    default: false,
  },
})

const slots = useSlots()
const hasTrailing = computed(() => !!slots.trailing)

// A trailing element takes an `auto` column and lets the label flex (`1fr`) so
// the trailing content right-aligns; without one, the suffix stays icon-width.
const gridColsClass = computed(() => {
  if (hasPrefixColumn.value) {
    return hasTrailing.value
      ? 'grid-cols-[0.875rem_1fr_auto]'
      : 'grid-cols-[0.875rem_auto_0.875rem]'
  }
  return hasTrailing.value
    ? 'grid-cols-[1fr_auto]'
    : 'grid-cols-[auto_0.875rem]'
})

const closeMenu = inject<() => void>('closeMenu', () =>
  console.warn('No closeMenu function provided'),
)

// A disabled item is never a link — there's nothing to navigate to, and it must
// not be reachable/activatable.
const isLink = computed(() => props.to !== undefined && !props.disabled)
const NuxtLink = resolveComponent('NuxtLink')
const rootComponent = computed(() => (isLink.value ? NuxtLink : 'div'))

function onClick() {
  if (props.disabled) return
  if (props.command) props.command()
  // Close on activation for command items and link navigation, but leave a bare
  // submenu trigger (no `command`, no `to`) alone — it opens on hover.
  if (props.command || isLink.value) closeMenu()
}

const hasPrefixColumn = inject<Ref<boolean>>('hasPrefixColumn', ref(false))
const setPrefixColumnTrue = inject<() => void>('setPrefixColumnTrue', () => {})

onMounted(() => {
  if (props.icon || props.selected !== undefined) {
    setPrefixColumnTrue()
  }
})
</script>
